import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { AlarmClock, CheckSquare } from 'lucide-react';
import clsx from 'clsx';
import { TASK_FIELD_LABELS, type Message } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useDisplayName, useProjects } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Button, Field, Input, Modal, Select, Textarea } from '@/components/ui';
import { usePlainText } from '@/features/chat/MessageBody';
import { remindChoices } from './remindChoices';
import { TaskForFields, taskForDefaults, type TaskFor } from './TaskForFields';
import { MilestoneField } from './taskExtras';

export type MessageTaskMode = 'task' | 'remind';

/**
 * t134: one dialog for "Create task from message" and "Remind me about this". Task = a shared task (project or
 * none, assignees, building); Remind me = a private task only you see, reminded at the time you pick. Both link back
 * to the message.
 */
export function MessageTaskModal({ message, initialMode = 'task', onClose }: { message: Message; initialMode?: MessageTaskMode; onClose: () => void }) {
  const plain = usePlainText();
  const { userId } = useAuth();
  const senderName = useDisplayName();
  const openTask = useUi(s => s.openTask);
  const toast = useUi(s => s.toast);
  const projects = useProjects();
  const editable = useMemo(() => (projects.data?.projects ?? []).filter(p => !p.archived_at && (p.my_role === 'owner' || p.my_role === 'editor')), [projects.data]);
  const text = plain(message.body).replace(/\r/g, '').trim();
  const firstLine = text.split('\n')[0].replace(/\s+/g, ' ').trim();
  const [mode, setMode] = useState<MessageTaskMode>(initialMode);
  const [title, setTitle] = useState(firstLine ? (firstLine.length > 200 ? `${firstLine.slice(0, 197)}…` : firstLine) : `Follow up on ${senderName(message.sender_id)}'s message`);
  const [description, setDescription] = useState(text ? `> ${text.split('\n').join('\n> ')}\n\n(from ${senderName(message.sender_id)}'s message)` : '');
  const [projectId, setProjectId] = useState('');
  const [milestone, setMilestone] = useState('');
  const [forWho, setForWho] = useState<TaskFor>(taskForDefaults(userId));
  const choices = useMemo(() => remindChoices(), []);
  const [pick, setPick] = useState(choices[0].id);
  const [date, setDate] = useState(choices[0].date);
  const [time, setTime] = useState(choices[0].time);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'remind') {
        const c = choices.find(x => x.id === pick);
        const when = pick === 'custom' ? { date, time } : { date: c!.date, time: c!.time };
        await api.convertToTask(message.id, { title: title.trim().slice(0, 300), description: '', is_private: true, due_date: when.date, due_time: when.time || null, reminder_minutes: 0 });
        toast(`We'll remind you ${pick === 'custom' ? `on ${DateTime.fromISO(when.date).toFormat('d LLL')}${when.time ? ` at ${when.time}` : ''}` : c!.label.replace(/ \(.*/, '').toLowerCase()}`, 'success');
      } else {
        const t = await api.convertToTask(message.id, { project_id: projectId || null, title: title.trim(), description, ...forWho, ...(projectId && milestone ? { milestone_id: milestone } : {}) });
        toast(`Created task: ${t.title}`, 'success');
        void queryClient.invalidateQueries({ queryKey: qk.taskLinks(message.conversation_id) });
        openTask(t.id);
      }
      void queryClient.invalidateQueries({ queryKey: ['tasks-area'] });
      onClose();
    } catch (e) { toast(errorMessage(e, mode === 'remind' ? "Couldn't add the reminder" : "Couldn't create the task", TASK_FIELD_LABELS), 'error'); } finally { setBusy(false); }
  };

  const tab = (m: MessageTaskMode, icon: React.ReactNode, label: string) => (
    <button type="button" role="tab" aria-selected={mode === m} onClick={() => setMode(m)} data-testid={`message-task-mode-${m}`}
      className={clsx('flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium', mode === m ? 'bg-black text-white dark:bg-white dark:text-black' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800')}>
      {icon}{label}
    </button>
  );
  return (
    <Modal open onClose={onClose} title={mode === 'remind' ? 'Remind me about this' : 'Create task from message'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={!title.trim() || (mode === 'remind' && pick === 'custom' && !date)} onClick={() => void submit()} data-testid="message-task-submit">{mode === 'remind' ? 'Add reminder' : 'Create task'}</Button></>}>
      <div className="space-y-4" data-testid="message-task-modal">
        <div role="tablist" aria-label="Task or reminder" className="flex gap-1 rounded-lg border border-slate-200 p-1 dark:border-slate-700">
          {tab('task', <CheckSquare className="h-4 w-4" />, 'Task')}
          {tab('remind', <AlarmClock className="h-4 w-4" />, 'Just remind me')}
        </div>
        <Field label="Title"><Input autoFocus value={title} onChange={e => setTitle(e.target.value)} maxLength={300} /></Field>
        {mode === 'task' ? <>
          <Field label="Project" hint={projectId ? undefined : "Only the people it's for can see a task with no project."}>
            <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
              <option value="">No project</option>
              {editable.map(p => <option key={p.id} value={p.id}>{p.name} ({p.key})</option>)}
            </Select>
          </Field>
          <Field label="Description"><Textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} /></Field>
          <TaskForFields value={forWho} onChange={setForWho} standalone={!projectId} me={userId}
            extra={<MilestoneField projectId={projectId} value={milestone} onChange={setMilestone} />} extraCount={milestone ? 1 : 0} />
        </> : (
          <fieldset>
            <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">When</legend>
            <div className="space-y-1.5 text-sm" role="radiogroup">
              {[...choices.map(c => ({ id: c.id, label: c.label })), { id: 'custom', label: 'Pick a date and time' }].map(c => (
                <label key={c.id} className="flex items-center gap-2"><input type="radio" name="remind" checked={pick === c.id} onChange={() => setPick(c.id)} />{c.label}</label>
              ))}
            </div>
            {pick === 'custom' && <div className="mt-2 flex gap-2"><Input type="date" aria-label="Date" value={date} onChange={e => setDate(e.target.value)} /><Input type="time" aria-label="Time" value={time} onChange={e => setTime(e.target.value)} /></div>}
            <p className="mt-3 text-xs text-slate-500">A private task in <Link className="link" to="/tasks?visibility=private" onClick={onClose}>your tasks</Link>, only you can see it.</p>
          </fieldset>
        )}
      </div>
    </Modal>
  );
}
